import React from 'react';
import { Loader2 } from '../icons';
import styles from './SecondaryButton.module.css';

/**
 * Props for SecondaryButton.
 */
interface SecondaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: React.ReactNode;
}

/**
 * SecondaryButton — ghost/bordered treatment for secondary actions.
 * Visually distinct from PrimaryButton (AC-003).
 * Click guard: disabled HTML attr + pointer-events:none ensures onClick never fires
 * in disabled or loading states (AC-003).
 * Supports ref forwarding for focus-management scenarios.
 */
const SecondaryButtonBase = React.forwardRef<HTMLButtonElement, SecondaryButtonProps>(
  function SecondaryButton({ loading = false, disabled = false, children, className, ...rest }, ref) {
    const isInactive = loading || disabled;
    const classList: string[] = [styles.button ?? ''];
    if (disabled && !loading) classList.push(styles.buttonDisabled ?? '');
    if (loading) classList.push(styles.buttonLoading ?? '');
    if (className) classList.push(className);
    return (
      <button
        {...rest}
        ref={ref}
        disabled={isInactive}
        aria-busy={loading || undefined}
        className={classList.join(' ')}
      >
        {loading && <Loader2 size={16} className={styles.spinner} aria-hidden="true" />}
        {children}
      </button>
    );
  },
);

SecondaryButtonBase.displayName = 'SecondaryButton';
export const SecondaryButton = React.memo(SecondaryButtonBase);
SecondaryButton.displayName = 'SecondaryButton';
